import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import useStatuses from '../store/ReactQueries/statuses/useStatus';
import useCategory from '../store/ReactQueries/categories/useCategory';
import useLocations from '../store/ReactQueries/locations/useLocation';
import useCustomers from '../store/ReactQueries/customers/useCustomer';
import usePriorities from '../store/ReactQueries/priorities/usePriority';
import useCompany from '../store/ReactQueries/general/company/useCompany';
import useContractors from '../store/ReactQueries/contractors/useContractor';
import useInterventionTypes from '../store/ReactQueries/interventionTypes/useInterventionType';
import useVendorStatus from '../store/ReactQueries/procurements/vendorStatuses/useVendorStatus';
import useAssetActionTypes from '../store/ReactQueries/assets/assetActionTypes/useAssetActionTypes';
import usePurchaseOrderTypes from '../store/ReactQueries/procurements/purchaseOrderTypes/usePurchaseOrderTypes';
import { GridColDef } from '@mui/x-data-grid';
import { FieldErrors } from '../domain/helpers/Errors';
import { useDarkModeStore } from '../store/useDarkModeStore';
import { useLanguageStore } from '../store/useLanguageStore';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { darkModeBackgroud, darkModeBackgroudInputs } from '../utils/constants';

interface CustomSelectProps<T> {
    form: T;
    view: boolean;
    column: GridColDef;
    errors: FieldErrors;
    label: string | undefined;
    setForm: React.Dispatch<React.SetStateAction<T>>;
}
export const CustomSelect = <T,>({ column, form, label, errors, view = false, setForm }: CustomSelectProps<T>) => {

    const fieldError = errors?.[column.field as keyof FieldErrors];

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const { data: statuses } = useStatuses(0, 0);
    const { data: companies } = useCompany(0, 0);
    const { data: categories } = useCategory(0, 0);
    const { data: locations } = useLocations(0, 0);
    const { data: customers } = useCustomers(0, 0);
    const { data: priorities } = usePriorities(0, 0);
    const { data: contractors } = useContractors(0, 0);
    const { data: vendorStatuses } = useVendorStatus(0, 0);
    const { data: assetActionTypes } = useAssetActionTypes(0, 0);
    const { data: interventionTypes } = useInterventionTypes(0, 0);
    const { data: purchaseOrderTypes } = usePurchaseOrderTypes(0, 0);

    const getDataByField = (field: string) => {
        switch (field) {
            case 'categoryId':
                return categories;
            case 'interventionTypeId':
                return interventionTypes;
            case 'locationId':
                return locations;
            case 'priorityId':
                return priorities;
            case 'customerId':
                return customers;
            case 'contractorId':
                return contractors;
            case 'statusId':
                return statuses;
            case 'assetActionTypeId':
                return assetActionTypes;
            case 'vendorStatusId':
                return vendorStatuses;
            case 'purchaseOrderTypeId':
                return purchaseOrderTypes;
            case 'companyId':
                return companies;
            default:
                return null;
        }
    };

    const onSelectionChange = (event: SelectChangeEvent<NonNullable<T[keyof T]>>) => {
        const { target } = event;
        const { name, value } = target;
        const baseName = name.slice(0, -2);
        const data = getDataByField(name);
        const selectedItem = data?.find((item) => item.id === value);

        if (selectedItem) {
            const updatedForm = { ...form, [name]: value, [baseName]: { Title: selectedItem.title } };
            setForm(updatedForm);
        }
        delete errors?.[name];
    };

    return (
        <FormControl fullWidth className='mt-2 mb-2' >
            <InputLabel
                id={column.headerName}
                style={darkMode ? { background: darkModeBackgroud, color: 'white' } : {}}
            >
                {label ? language[label] : label}
            </InputLabel>
            <Select
                readOnly={view}
                name={column.field}
                id={column.headerName}
                labelId={column.headerName}
                error={Boolean(errors?.[column.field])}
                label={label ? language[label] : label}
                value={form[column.field as keyof typeof form] || ''}
                style={darkMode ? { background: darkModeBackgroud, color: 'white' } : {}}
                onChange={(event) => onSelectionChange(event)}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            backgroundColor: darkMode ? darkModeBackgroudInputs : 'white',
                            color: darkMode ? 'white' : 'black',
                        },
                    },
                }}
            >
                {getDataByField(column.field)?.map(item => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
            </Select>
            {
                fieldError
                && <p
                    className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained css-1wc848c-MuiFormHelperText-root"
                    style={{ color: 'red', marginTop: '0.5rem' }}
                >
                    {fieldError}
                </p>
            }
        </FormControl>
    );
}
export default CustomSelect